import { AnyAction } from "redux"
import { ThunkAction } from "redux-thunk"
import { fetchKycFlowsThunk, getText, requestThunk, TOrganizationType, TPayment } from "swiipe.portal.shared"
import { endpoints } from "../../data/endpoints"
import { merchantServicesSelectors } from "../reducers/merchantServicesReducer"
import { StoreState } from "../StoreState"
import { getGatewaysForMerchantThunk } from "./gatewayThunks"
import { sendServiceConfirmationEmailThunk } from "./swiipeServicesThunks"

export interface IDigitealGetOnboardingDataResponse {}

export const getDigitealOnboardingDataThunk =
    (webshopId: string): ThunkAction<Promise<IDigitealGetOnboardingDataResponse>, StoreState, null, AnyAction> =>
    async (dispatch) => {
        return await dispatch(
            requestThunk<IDigitealGetOnboardingDataResponse>(endpoints.Digiteal.getOnboardingData, {
                params: {
                    webshopId: webshopId,
                },
            })
        )
    }

export interface IDigitealOnboardingConnectData {
    swMerchantId: string
    webshopIds: string[]
    paymentType: TPayment
}

export const digitealOnboardingConnectThunk =
    (data: IDigitealOnboardingConnectData): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        await dispatch(getGatewaysForMerchantThunk(data.swMerchantId, false, false))
        const notOnboardedWebshopIds = data.webshopIds.filter((wid) => {
            return !merchantServicesSelectors
                .statuses(getState(), data.swMerchantId || "", wid)
                ?.gatewayProviders.find(
                    (p) =>
                        p.providerName === "Digiteal" && p.paymentType === data.paymentType && p.status === "Ready" && !!p.enabled
                )
        })

        if (notOnboardedWebshopIds.length === 0) {
            return
        }

        await dispatch(
            requestThunk(endpoints.Digiteal.onboardConnect, {
                data: {
                    ...data,
                    webshopIds: notOnboardedWebshopIds,
                },
            })
        )

        switch (data.paymentType) {
            case "Bancontact":
                await dispatch(
                    sendServiceConfirmationEmailThunk(
                        data.swMerchantId,
                        "BancontactConfirmation",
                        getText("serviceNames.bancontact")
                    )
                )
                break
            case "Ideal":
                await dispatch(
                    sendServiceConfirmationEmailThunk(data.swMerchantId, "IdealConfirmation", getText("serviceNames.ideal"))
                )
                break
            case "CreditCard":
                break
        }

        await dispatch(getGatewaysForMerchantThunk(data.swMerchantId, true, false))
    }

export const checkDigitealCompanyTransferThunk =
    (organizationId: string, organizationType: TOrganizationType): ThunkAction<Promise<boolean>, StoreState, null, AnyAction> =>
    async (dispatch) => {
        const resp = await dispatch(
            requestThunk<{ transferredOrganizationIds: string[] }>(endpoints.Digiteal.checkCompanyTransfer, {
                data: {
                    limitToOrganizationId: organizationId,
                },
            })
        )
        await dispatch(fetchKycFlowsThunk(organizationId, organizationType, true))
        return resp.transferredOrganizationIds.includes(organizationId)
    }

export const testTriggerDigitealWebhookThunk =
    (
        organizationId: string,
        organizationType: TOrganizationType,
        webhookName: "companyValidated" | "companyRejected"
    ): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch) => {
        await dispatch(
            requestThunk(endpoints.Digiteal.testTriggerWebhook, {
                data: {
                    organizationId,
                    webhookName,
                    comment: undefined,
                },
            })
        )
        await dispatch(fetchKycFlowsThunk(organizationId, organizationType, true))
    }
